import {gql} from '@apollo/client';
import {get as _get, sortBy as _sortBy, isEmpty as _isEmpty, isEqual as _isEqual, isUndefined as _isUndefined} from 'lodash';

import DefaultForm from '@appComponents/DefaultForm';
import {getLink} from '@appComponents/Link';
import {PresetsQuery} from '@graphql/eventPreset/query';
import {rightsScopesDates as GraphQLRightsScopes} from '@graphql/propertyLicences/rightsScopes';
import {getCoverageOptions} from '@graphql/rightsScope/query';
import {GetStadiumsByTournaments} from '@graphql/stadium/query';
import {setErrorModal, showErrorModal} from '@utils/modal';
import {transformMomentDatetimeToDate, formatDateWithoutSeconds} from '@utils/date';
import {convertToInt} from '@utils/helpers';
import {sortDropdownOptionsAlphanumerically} from '@utils/sorters';
import {
    getDefaultClipProvidersValues,
    getEventContentTypesFromClipConfigOptions,
    getClipProvidersDefaultsFromEvent,
    getCoverageValuesFromPresetProducts,
    normalizeClipProvidersValues,
    prefillCoverage,
    resolveClipProvidersDefaults,
    setClipProvidersInForm,
    updateClipProvidersFieldBasedOnPreset,
    hasArgosJobScheduling,
    hasSpiideoIntegration,
} from '@modules/events/utils/formHelper';
import {
    COLOR_DEFAULT,
    EVENT_NON_SPORTRADAR_TYPE,
    EVENT_SPORTRADAR_TYPE,
    SPORTRADAR_EXISTING_EVENT,
} from '@constants/variables';
import {isValidDate} from '@utils/filters';
import {fetchRightsScopesDatesRange} from '@modules/events/forms/ExistingStageEvent/stageFormHelper';

import {SPORTRADAR_EXISTING_EVENT_ADD_FORM_ID} from './SportradarExistingEventByMatchModel';
import {renderModalErrorObject, setCanDeleteState} from '../utils/eventContentHelper';
import RightScope from './logic/RightScope';
import {CANT_DELETE_EVENT} from '../constants/messages';

class EventForm extends DefaultForm {
    editCoveragePrefillSynced = false;
    lastCoverageRequestKey = null;
    lastClipProvidersCoverageKey = null;

    addPresetDataForSubmit = (dataToSave, formData = {}) => {
        dataToSave.preset = {};

        if (!formData.preset_save) {
            return dataToSave;
        }

        const preset = {
            name: formData.preset_name,
        };

        if (formData.preset_id) {
            preset.id = convertToInt(formData.preset_id);
        }

        dataToSave.preset = preset;

        return dataToSave;
    };

    backToEventPage = () => {
        this.props.history.push(getLink('events.configuration.index', {id: this.props.formParams.id}));
    };

    getPresetsForPropertyLicence = (propertyLicenceId = null, eventId = null) => {
        this.setField('event_preset', {
            loading: true,
            defaultValue: null,
        });

        let presetsListObject = {};

        this.runApolloRequest('query', {
            fetchPolicy: 'network-only',
            query: PresetsQuery,
            variables: {
                propertyLicence: propertyLicenceId && [convertToInt(propertyLicenceId)],
                excludeEvent: eventId && parseInt(eventId, 10),
            },
        }).then((response) => {
            let optionsForDropdown = [],
                presetsObject = {};

            let usedPreset = _get(this.state, 'defaultForm_data.event.event_preset.id', null);

            response.data.eventPresets.forEach((preset) => {
                presetsListObject[preset.id] = preset;

                optionsForDropdown.push({
                    key: preset.id,
                    value: preset.id,
                    text: preset.name + (usedPreset === preset.id ? ' (last applied preset)' : ''),
                });

                presetsObject[preset.id] = preset.event;

            });

            this.setState(() => ({
                presetsListObject,
            }), () => {
                this.setField('event_preset', {
                    disabled: false,
                    loading: false,
                    options: optionsForDropdown,
                });
            });
        });
    };

    getContentCategoriesOptionsForDropdown = (propertyLicenceId, contentCategories, rightsScopes) => {
        let categoriesWithRightScopes = [],
            contentCategoriesArray = _get(contentCategories, `${propertyLicenceId}`, []);

        if (rightsScopes.all) {
            categoriesWithRightScopes = contentCategoriesArray;

            return categoriesWithRightScopes;
        }

        contentCategoriesArray.forEach((item) => {
            if (rightsScopes[parseInt(item.id)]) {
                categoriesWithRightScopes.push(item);
            }
        });

        return categoriesWithRightScopes;
    };

    setContentCategoryOptions = (propertyLicenceId, options = this.state.contentCategoriesOptions) => {
        this.setField('content_category', {
            disabled: true,
            loading: true,
            defaultValue: null,
        });

        this.getRightsScopes(propertyLicenceId).then((response) => {
            if (!propertyLicenceId) {
                return [];
            }

            this.setField('content_category', {
                disabled: false,
                loading: false,
                options: this.getContentCategoriesOptionsForDropdown(propertyLicenceId, options, response),
            });
        });
    };

    setCompetitorField = (competitor, eventType = EVENT_SPORTRADAR_TYPE) => {
        let eventKey = 'sr_event';

        if (eventType === EVENT_NON_SPORTRADAR_TYPE) {
            eventKey = 'av_event';
        }

        const competitorObject = _get(this.state.defaultForm_data, `event.${eventKey}.${competitor}`, null);

        competitorObject && this.setField(`${competitor}`, {
            options: [{
                key: competitorObject.id,
                text: `${competitorObject.name} (${competitorObject.id})`,
                value: competitorObject.id,
            }],
        });
    };

    getStadiumsByTournamentId = (tournamentId) => (
        this.runApolloRequest('query', {
            fetchPolicy: 'network-and-cache',
            query: GetStadiumsByTournaments,
            variables: {
                tournaments: [convertToInt(tournamentId)],
            },
        })
    );

    getStadiumsByTournamentData = (tournamentId) => (
        this.getStadiumsByTournamentId(tournamentId).then((response) => (
            sortDropdownOptionsAlphanumerically(response.data.stadiums.map(stadium => ({
                key: stadium.id,
                text: `${stadium.name}${stadium.city ? ` - ${stadium.city.name}` : ''} (${stadium.id})`,
                value: stadium.id,
            })))
        )).catch(() => {
            this.props.MessageBox.addMessage('formInnerErrorMessage', null, 'Can\'t get stadiums data.', 'error');
        })
    );

    getRightsScopes = (propertyLicenceId) => {
        return this.runApolloRequest('query', {
            fetchPolicy: 'network-only',
            query: gql(GraphQLRightsScopes),
            variables: {
                propertyLicenceId: parseInt(propertyLicenceId, 10),
            },
        }).then((response) => {
            this.setState(() => ({clipProvidersOptions: response.data.clipProviders}));
            this.setClipProvidersField({options: response.data.clipProviders});

            return this.setRightScopes(response.data.rightsScopes);
        }).catch(() => {
            this.setFormMessage('Can\'t get right scopes for selected property licence.');
        });
    };

    setRightScopes(data, key) {
        let rightsScopes = RightScope.perapreRightScopes(data, key);

        this.setState(() => ({
            rightsScopes: rightsScopes,
        }));

        return rightsScopes;
    }

    filterEndDatetime = (date) => {
        date = transformMomentDatetimeToDate(date);

        let startDatetime = _get(this.props, 'formValues.start_datetime', null);

        if (startDatetime) {
            startDatetime = startDatetime.substring(0, 10);
        }

        return date.isSameOrAfter(startDatetime);
    };

    resetCoverageField = () => {
        this.lastCoverageRequestKey = null;

        this.setField('coverage', {
            options: [],
            defaultValue: [],
            timestamp: Date.now(),
        });
    };

    buildCoverageRequestKey = (propertyLicenceId, tournamentId, contentCategoryId, startDate) => {
        return [
            propertyLicenceId ?? '',
            tournamentId ?? '',
            contentCategoryId ?? '',
            startDate ?? '',
        ].join('|');
    };

    getCoverageOptions = (propertyLicenceId, tournamentId, contentCategoryId, startDate,stageCategory) => {
        this.runApolloRequest('query', {
            fetchPolicy: 'network-only',
            query: getCoverageOptions,
            variables: {
                propertyLicence: convertToInt(propertyLicenceId),
                tournament: convertToInt(tournamentId),
                contentCategory: convertToInt(contentCategoryId),
                startDate: startDate && formatDateWithoutSeconds(startDate),
                stageCategory: stageCategory,
            },
        }).then(({data}) => {
            const {coverageOptions: {products}} = data;

            this.setState(() => ({
                coverageOptions: products,
            }));

            if (!_isEmpty(products)) {
                this.setField('coverage', {
                    hidden: false,
                    options: products,
                });

                if (this.syncPresetCoverageToForm(this.state.selectedPresetData, products)) {
                    this.setClipProvidersField({
                        defaultDropdownValues: getClipProvidersDefaultsFromEvent(this.state.selectedPresetData.event),
                    });
                    setClipProvidersInForm(
                        this.props.change,
                        this.state.eventContentTypes,
                        getClipProvidersDefaultsFromEvent(this.state.selectedPresetData.event)
                    );
                }
            } else {
                this.hideCoverageField();
            }
        }).catch(() => {
            if (!propertyLicenceId) {
                this.setFormMessage('Invalid parameters. Verify the property licence.');
            }

            if (!startDate) {
                this.setFormMessage('Invalid parameters. Verify start date.');
            }

            if (!tournamentId || !contentCategoryId || !stageCategory) {
                this.setFormMessage('Invalid parameters. Verify the tournament/content category.');
            }
        });
    };

    syncPresetCoverageToForm = (selectedPresetData, coverageOptions = this.state.coverageOptions || []) => {
        const presetProducts = selectedPresetData?.event?.products;
        const effectiveCoverageOptions = (coverageOptions && 0 < coverageOptions.length)
            ? coverageOptions
            : this.getCoverageOptionsForClipProviders();

        if (!Array.isArray(presetProducts)) {
            return false;
        }

        if (0 === presetProducts.length) {
            this.clearPresetCoverageAndClipProviders();

            return true;
        }

        const syncedCoverage = getCoverageValuesFromPresetProducts(effectiveCoverageOptions, presetProducts);

        this.setField('coverage', {
            defaultValue: syncedCoverage,
            timestamp: Date.now(),
        });
        this.props.change && this.props.change('coverage', syncedCoverage);

        if (0 === syncedCoverage.length && !_isEmpty(this.props.formValues.clip_providers)) {
            this.props.change && this.props.change('clip_providers', {});
        }

        return true;
    };

    clearPresetCoverageAndClipProviders = () => {
        this.setField('coverage', {
            defaultValue: [],
            timestamp: Date.now(),
        });
        this.setClipProvidersField({
            hidden: true,
            defaultDropdownValues: {},
        });
        this.lastClipProvidersCoverageKey = null;
        this.props.change && this.props.change('coverage', []);
        this.props.change && this.props.change('clip_providers', {});
    };

    /**
     * Shared preset/coverage sync logic for componentDidUpdate in subforms.
     * Call from each subform's componentDidUpdate after form-specific logic.
     */
    handlePresetCoverageSync(prevProps, prevState = {}) {
        const {event_preset} = this.props.formValues;
        const isCoverageFieldHidden = _get(this.state, 'defaultForm_fields.coverage.props.hidden', false);
        const eventPresetChanged = prevProps.formValues.event_preset !== event_preset;
        const selectedPresetDataChanged = this.state.selectedPresetData !== prevState.selectedPresetData;

        if ((eventPresetChanged || selectedPresetDataChanged) && event_preset && this.props.change) {
            const presetProducts = this.state.selectedPresetData?.event?.products;
            const hasPresetProductsLoaded = Array.isArray(presetProducts);
            const coverageOptions = _get(this.state, 'defaultForm_fields.coverage.props.options', this.state.coverageOptions || []);
            const syncedCoverage = getCoverageValuesFromPresetProducts(coverageOptions, presetProducts || []);

            if (hasPresetProductsLoaded && 0 === presetProducts.length) {
                this.clearPresetCoverageAndClipProviders();
            }

            if (hasPresetProductsLoaded && 0 < presetProducts.length && !_isEqual(this.props.formValues.coverage || [], syncedCoverage)) {
                this.props.change('coverage', syncedCoverage);
                this.setField('coverage', {
                    defaultValue: syncedCoverage,
                    timestamp: Date.now(),
                });
            }

            if (hasPresetProductsLoaded && 0 === syncedCoverage.length && !_isEmpty(this.props.formValues.clip_providers)) {
                this.props.change('clip_providers', {});
            }
        }

        if ((eventPresetChanged || selectedPresetDataChanged) && !event_preset) {
            prefillCoverage(
                isCoverageFieldHidden,
                this.state.selectedPresetData?.event?.products,
                _get(this.state, 'defaultForm_fields.coverage.props.options', []),
                this.setField,
                this.state.commonProducts,
                (newState) => this.setState(newState)
            );
        }

        if (eventPresetChanged || selectedPresetDataChanged) {
            this.setState({presetApplied: !!event_preset});
        }

        if (event_preset && !isCoverageFieldHidden && !this.skipPresetClipProvidersUpdate) {
            updateClipProvidersFieldBasedOnPreset(this.props, prevProps.formValues, this.setField, this.state, this.state.presetsListObject, {
                setFormValue: this.props.change,
            });
        }

        if (!event_preset) {
            this.skipPresetClipProvidersUpdate = false;
        }
    }

    areParamsValid = (prevProps, tournamentId, contentCategory, checkContentCategory, isExistingForm) => {
        const {property_licence, start_datetime, id, stage_category_id} = this.props.formValues;
        const hasValidStartDatetime = isValidDate(start_datetime);
        const hasPropertyLicenceChanged = prevProps.formValues.property_licence !== property_licence;
        const hasStartDatetimeChanged = formatDateWithoutSeconds(start_datetime)
            !== formatDateWithoutSeconds(prevProps.formValues.start_datetime);
        const hasContentCategoryChanged = contentCategory !== prevProps.formValues.content_category;
        const hasTournamentChanged = tournamentId !== prevProps.formValues.tournament_id;
        const shouldBootstrapCoverage = _isEmpty(this.state.coverageOptions);
        const hasStageCategoryChanged = stage_category_id && stage_category_id !== prevProps.formValues.stage_category_id;

        if (property_licence && (tournamentId || contentCategory || stage_category_id) && hasValidStartDatetime && hasStartDatetimeChanged && !id) {
            this.resetCoverageField();
        }

        return property_licence && (tournamentId || contentCategory || hasStageCategoryChanged) && hasValidStartDatetime
            && (hasStartDatetimeChanged
                || (!isExistingForm && checkContentCategory && hasContentCategoryChanged)
                || (!isExistingForm && !checkContentCategory && hasTournamentChanged)
                || (isExistingForm && hasPropertyLicenceChanged)
                || shouldBootstrapCoverage
            )
            || (property_licence && !_isUndefined(stage_category_id) && hasStartDatetimeChanged && hasValidStartDatetime)
            || (hasPropertyLicenceChanged && stage_category_id && start_datetime);
    };

    setCoverageField = (prevProps, tournamentId, contentCategory, checkContentCategory, isExistingForm) => {
        const formValues = this.props.formValues;
        const isEditFlow = this.isEditFlow();
        const stageCategory = convertToInt(formValues.stage_category_id) ?? null;

        if (isEditFlow) {
            this.prefillCoverageOnEditForm();
        }

        if (this.areParamsValid(prevProps, tournamentId, contentCategory, checkContentCategory, isExistingForm)) {
            const coverageRequestKey = this.buildCoverageRequestKey(
                formValues.property_licence,
                tournamentId,
                contentCategory,
                formValues.start_datetime
            );

            if (coverageRequestKey === this.lastCoverageRequestKey) {
                return;
            }

            this.lastCoverageRequestKey = coverageRequestKey;

            this.getCoverageOptions(
                formValues.property_licence,
                tournamentId,
                contentCategory,
                formValues.start_datetime,
                stageCategory
            );

            if (isEditFlow) {
                const hasStartDatetimeChanged = formatDateWithoutSeconds(this.props.formValues.start_datetime)
                    !== formatDateWithoutSeconds(prevProps.formValues.start_datetime);

                if (prevProps.formValues.start_datetime && hasStartDatetimeChanged
                    && this.props.form !== SPORTRADAR_EXISTING_EVENT) {
                    this.resetCoverageField();
                }
            }
        }
    };

    prefillCoverageOnEditForm() {
        if (this.state.selectedPresetData || this.state.presetApplied) {
            return;
        }

        const selectedProducts = this.props.GraphQLEntityData?.event?.products?.map(
            (product) => convertToInt(product.id)
        );

        if (!_isEmpty(selectedProducts)) {
            const currentCoverageDefault = _get(this.state, 'defaultForm_fields.coverage.props.defaultValue', []);
            const coverageIsHidden = _get(this.state, 'defaultForm_fields.coverage.props.hidden', false);

            if (!_isEqual(currentCoverageDefault, selectedProducts) && !coverageIsHidden) {
                this.setField('coverage', {
                    defaultValue: selectedProducts,
                });
            }

            if (!_isEqual(this.props.formValues.coverage, selectedProducts)
                && !this.editCoveragePrefillSynced
                && this.props.change) {
                this.props.change('coverage', selectedProducts);
            }

            this.editCoveragePrefillSynced = true;
        }
    }

    isEditFlow = () => {
        return this.props.formValues?.id
            || (this.props.Model?.formId !== SPORTRADAR_EXISTING_EVENT_ADD_FORM_ID && this.props.formParams?.id)
            || this.props.Model?.editForm
            || this.props.Model?.editModel
            || this.state?.defaultForm_data?.event?.id;
    };

    toggleClipProvidersDropdown(prevProps, prevState = {}) {
        const {formValues} = this.props;
        const {start_datetime} = formValues;
        const isCoverageFieldHidden = _get(this.state, 'defaultForm_fields.coverage.props.hidden', false);
        const wasCoverageFieldHidden = _get(prevState, 'defaultForm_fields.coverage.props.hidden', false);
        const isClipProvidersFieldHidden = _get(this.state, 'defaultForm_fields.clip_providers.props.hidden', true);
        const hadEventContentTypes = 0 < (this.prevEventContentTypesLength ?? 0);
        const eventContentTypes = this.getClipProvidersEventContentTypes();
        const hasEventContentTypes = 0 < eventContentTypes.length;
        const didEventContentTypesBecomeAvailable = !hadEventContentTypes && hasEventContentTypes;
        const didEventContentTypesBecomeEmpty = hadEventContentTypes && !hasEventContentTypes;
        const didCoverageFieldBecomeVisible = wasCoverageFieldHidden && !isCoverageFieldHidden;
        const didCoverageFieldBecomeHidden = !wasCoverageFieldHidden && isCoverageFieldHidden;
        const didEventPresetChange = prevProps.formValues.event_preset !== formValues.event_preset;
        const coverage = formValues.coverage;
        const previousCoverage = prevProps.formValues.coverage;
        const hasCoverageChanged = !_isEqual(coverage, previousCoverage);
        const hadCoverage = !_isEmpty(previousCoverage);
        const hasCoverage = !_isEmpty(coverage);
        const isManualCoverageSelectionAfterPreset = hasCoverageChanged && hasCoverage && !!formValues.event_preset;
        const coverageKey = (coverage ?? []).join('|');
        const shouldBootstrapVisible = hasCoverage
            && isClipProvidersFieldHidden
            && coverageKey !== this.lastClipProvidersCoverageKey;
        const shouldShowClipProvidersField = !isCoverageFieldHidden
            && hasCoverage
            && (hasCoverageChanged
                || didEventContentTypesBecomeAvailable
                || didEventContentTypesBecomeEmpty
                || didCoverageFieldBecomeVisible
                || didEventPresetChange
                || shouldBootstrapVisible)
            && (start_datetime || this.isEditFlow());

        if (shouldShowClipProvidersField) {
            if (hasEventContentTypes) {
                this.setClipProviders({forceUpdate: isManualCoverageSelectionAfterPreset});
            } else {
                this.setClipProvidersField({defaultDropdownValues: {}});
                setClipProvidersInForm(this.props.change, [], {});
            }

            this.setClipProvidersField({hidden: false});
            this.lastClipProvidersCoverageKey = coverageKey;
        } else if (didCoverageFieldBecomeHidden
            || (hadCoverage && hasCoverageChanged && !hasCoverage)
            || (hasCoverageChanged && !hasCoverage && !isClipProvidersFieldHidden)) {
            this.setClipProvidersField({hidden: true});
            this.lastClipProvidersCoverageKey = null;
        }

        this.prevEventContentTypesLength = eventContentTypes.length;
    }

    setClipProviders = ({forceUpdate = false} = {}) => {
        let selectedClipProvider = '';
        let defaultDropdownValues = {};
        let selectedCoverages = this.props.formValues.coverage;
        const coverageOptions = this.getCoverageOptionsForClipProviders();
        const eventContentTypes = this.getClipProvidersEventContentTypes();
        const formClipProviders = normalizeClipProvidersValues(this.props.formValues.clip_providers ?? {});
        const isEditFlow = this.isEditFlow();

        if (!isEditFlow) {
            selectedCoverages.forEach((coverageId) => {
                const coverageOption = coverageOptions?.find(option => Number(option.id ?? option.value) === Number(coverageId));

                if (coverageOption?.providers?.[0]) {
                    selectedClipProvider = (coverageOption.providers[0]).toString();
                }
            });
            defaultDropdownValues = resolveClipProvidersDefaults(
                formClipProviders,
                getDefaultClipProvidersValues(eventContentTypes, selectedClipProvider)
            );
        } else if (isEditFlow) {
            selectedCoverages.forEach((coverageId) => {
                const coverageOption = coverageOptions?.find(option => Number(option.id ?? option.value) === Number(coverageId));

                if (coverageOption?.providers?.[0]) {
                    selectedClipProvider = (coverageOption.providers[0]).toString();
                }
            });
            defaultDropdownValues = resolveClipProvidersDefaults(
                formClipProviders,
                getClipProvidersDefaultsFromEvent(this.props.GraphQLEntityData?.event)
            );
            selectedClipProvider = Object.values(defaultDropdownValues)[0] ?? '';
        }

        if (!this.state.presetApplied || forceUpdate) {
            this.setClipProvidersField({
                defaultDropdownValues,
            });
            setClipProvidersInForm(this.props.change, eventContentTypes, defaultDropdownValues);
        }

        this.setState(() => ({clipProviderFromPropertyLicence: selectedClipProvider}));
    };

    setClipProvidersField = (props = {}) => {
        this.setField('clip_providers', {
            options: this.getClipProvidersOptions(),
            eventContentTypes: this.getClipProvidersEventContentTypes(),
            ...props,
        });
    };

    getClipProvidersOptions = () => {
        return !_isEmpty(this.state.clipProvidersOptions)
            ? this.state.clipProvidersOptions
            : _get(this.state, 'defaultForm_fields.clip_providers.props.options', []);
    };

    getClipProvidersEventContentTypes = () => {
        return !_isEmpty(this.state.eventContentTypes)
            ? this.state.eventContentTypes
            : _get(this.state, 'defaultForm_fields.clip_providers.props.eventContentTypes', []);
    };

    getCoverageOptionsForClipProviders = () => {
        return !_isEmpty(this.state.coverageOptions)
            ? this.state.coverageOptions
            : _get(this.state, 'defaultForm_fields.coverage.props.options', []);
    };

    loadEventContentTypes = async (tournamentIds = [], sportIds = []) => {
        const eventContentTypes = await getEventContentTypesFromClipConfigOptions(this.runApolloRequest, tournamentIds, sportIds);

        this.setState(() => ({eventContentTypes}));
        this.setClipProvidersField({eventContentTypes});

        return eventContentTypes;
    };

    handleArgosJobSchedulingField = (propertyLicences, propertyLicenceId) => {
        if (!propertyLicences || !propertyLicenceId) {
            return;
        }

        this.setField('argos_job_scheduling', {
            hidden: !hasArgosJobScheduling(propertyLicences, propertyLicenceId),
        });
    };

    handleSpiideoIntegrationField = (propertyLicences, propertyLicenceId) => {
        if (!propertyLicences || !propertyLicenceId) {
            return;
        }

        this.setField('spiideo_integration', {
            hidden: !hasSpiideoIntegration(propertyLicences, propertyLicenceId),
        });
    };

    updateAsAdditionalArgosField = () => {
        if (!this.props.Model.fields?.as_additional_argos) {
            return;
        }

        const {event_preset, argos_job_scheduling} = this.props.formValues;
        const isVisible = event_preset && argos_job_scheduling;

        this.setField('as_additional_argos', {
            hidden: !isVisible,
            ...(!isVisible && {defaultValue: false}),
        });
    };

    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps);

        const {event_preset, argos_job_scheduling} = this.props.formValues;

        if (event_preset !== prevProps.formValues?.event_preset
            || argos_job_scheduling !== prevProps.formValues?.argos_job_scheduling
        ) {
            this.updateAsAdditionalArgosField();
        }
    }

    hideCoverageField = () => {
        this.setField('coverage', {
            hidden: true,
            options: [],
            defaultValue: [],
            timestamp: Date.now(),
        });
    };

    setStateHandler = (stateObject) => {
        this.setState(() => (stateObject));
    };

    parsePropertyLicenceOptions = (data) => {
        let propertyLicences = [];

        if (Array.isArray(data)) {
            propertyLicences = data;
        } else if (data) {
            propertyLicences = [data];
        }

        let propertyLicencesList = [];

        for (const propertyLicence of propertyLicences) {
            let contents = _get(propertyLicence, 'contents', []);

            if (contents && 0 < contents.length
                || this.props.Model.formId === SPORTRADAR_EXISTING_EVENT_ADD_FORM_ID) {
                propertyLicencesList.push({
                    id: propertyLicence.id,
                    key: propertyLicence.id,
                    text: `${propertyLicence.name} - ${_get(propertyLicence, 'licensor.name', '')}`,
                    value: propertyLicence.id,
                });
            }
        }

        return _sortBy(propertyLicencesList, (item) => item.text.toLocaleLowerCase());
    };

    componentDidMount() {
        super.componentDidMount();

        const eventId = _get(
            this.props,
            'formParams.id',
            null
        );

        'undefined' == typeof this.state.canDelete
        && eventId
        && setCanDeleteState(eventId, this.setStateHandler, true);

        this.setOptionParsers({
            property_licence: this.parsePropertyLicenceOptions,
        });

        this.setOnChangeCallback({
            'preset_save': (data) => {
                let isPresetOverwriteHidden = true;

                if (!data.value) {
                    isPresetOverwriteHidden = false;
                }

                this.setField('preset_name', {
                    hidden: isPresetOverwriteHidden,
                });

                this.setField('preset_overwrite', {
                    hidden: !(!isPresetOverwriteHidden && !!this.props.formValues.event_preset),
                });
            },
            'preset_overwrite': (data) => {
                let eventPresetId = null;

                if (!data.value) {
                    eventPresetId = this.props.formValues.event_preset;
                }

                this.setField('preset_id', {
                    defaultValue: eventPresetId,
                });
            },
            'event_preset': (data) => {
                let eventPreset = _get(this.state, `presetsListObject.[${data.value}].event`),
                    color = _get(eventPreset, 'color', COLOR_DEFAULT),
                    isPresetOverwriteHidden = true;

                if (data.value) {
                    isPresetOverwriteHidden = false;
                }

                isPresetOverwriteHidden = !(!isPresetOverwriteHidden && !!this.props.formValues.preset_save);

                this.setField('preset_overwrite', {
                    hidden: isPresetOverwriteHidden,
                });

                let presetId = null;

                if (this.props.formValues.preset_overwrite) {
                    presetId = data.value;
                }

                this.setField('preset_id', {
                    defaultValue: presetId,
                });

                if (color) {
                    this.setField('color', {
                        defaultValue: '#' + color,
                    });
                }

                this.setField('description', {
                    defaultValue: _get(eventPreset, 'description', null),
                });

                this.setField('external_description', {
                    defaultValue: _get(eventPreset, 'external_description', null),
                });

                this.setField('event_status', {
                    defaultValue: _get(this.props, 'formValues.event_status', null),
                    value: _get(this.props, 'formValues.event_status', null),
                });

                this.setField('notes', {
                    defaultValue: _get(eventPreset, 'notes', ''),
                });

                this.setField('preset_name', {
                    defaultValue: _get(this.state, `presetsListObject.[${data.value}].name`, null),
                });

                this.setField('as_additional_contents', {
                    hidden: !data.value,
                });

                this.setField('argos_job_scheduling', {
                    defaultValue: _get(eventPreset, 'has_argos_job_scheduling', false),
                });

                if (this.props.Model.fields?.spiideo_integration) {
                    this.setField('spiideo_integration', {
                        defaultValue: _get(eventPreset, 'has_spiideo_integration', false),
                    });
                }

                const selectedPresetData = _get(this.state, `presetsListObject.[${data.value}]`, null);
                this.setState(() => ({
                    selectedPresetData: selectedPresetData,
                }));

                this.syncPresetCoverageToForm(selectedPresetData);
            },
        });

        this.setField('start_datetime', {
            configuration: {
                ..._get(this.props, 'Model.fields.start_datetime.props.configuration', {}),
                filterDate: this.filterStartDatetime,
                strictParsing: true,
            },
        });

        this.setField('end_datetime', {
            configuration: {
                ..._get(this.props, 'Model.fields.start_datetime.props.configuration', {}),
                filterDate: this.filterEndDatetime,
                strictParsing: true,
            },
        });

        this.setValuesParsers({
            color: (value) => {
                if (value) {
                    return '#' + value;
                }

                return null;
            },
        });

        this.setCreateSuccessCallback((response) => {
            this.props.history.push(getLink('events.configuration.index', {id: response.data.createSrEvent.event.id}));
        });
    }

    updateRightsScopesDatesRange = (property_licence) => {
        fetchRightsScopesDatesRange(property_licence)
            .then(response => {
                const dates = response.rightsScopes.flatMap(scope => [
                    new Date(scope.start_date),
                    new Date(scope.end_date),
                ]);
                const minDate = new Date(Math.min(...dates));
                const maxDate = new Date(Math.max(...dates));

                this.setField(['start_datetime', 'end_datetime'], {
                    configuration: {
                        ..._get(this.state, 'defaultForm_fields.start_datetime.props.configuration', {}),
                        filterDate: this.filterStartDatetime,
                        minDate,
                        maxDate,
                    },
                });

                if (!this.state.clipProvidersOptions) {
                    this.setState(() => ({clipProvidersOptions: response.clipProviders}));

                    this.setField('clip_providers', {
                        options: response.clipProviders,
                    });
                }
            })
            .catch(e => showErrorModal({
                header: 'Error',
                text: e.message,
            }));
    };

    redirectToEventEditPage = () => {
        window.location.href = getLink('events.edit', {
            id: _get(this.props, 'formParams.id', null),
        });
    };

    renderDeleteButton() {
        return this.state.canDelete
            ? super.renderDeleteButton()
            : super.renderDeleteButton({
                onClick: () => setErrorModal(
                    renderModalErrorObject(CANT_DELETE_EVENT, this.redirectToEventEditPage)
                ),
            });
    }
}

export default EventForm;
